import { useEffect, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ClientType } from '@econtractor/types';
import * as apiClient from 'api/apiClients';
import SolarIcon from 'components/icons/SolarIcon';
import EToolTip from 'components/others/EToolTip';
import ClientSelected from '../../ClientSelected';
import { useCustomersList } from '../context/CustomersListProvider';

const CustomersFilter = () => {
    const { t } = useTranslation(['Clients', 'common']);
    const { filterClientId, setFilterClientId, filterVatEnabled, setFilterVatEnabled } =
        useCustomersList();
    const [draftClient, setDraftClient] = useState<ClientType | null>(null);
    const [draftClientId, setDraftClientId] = useState<number | null>(filterClientId);
    const [draftVatEnabled, setDraftVatEnabled] = useState<0 | 1 | null>(filterVatEnabled);
    const [show, setShow] = useState(false);
    const filterActive = Boolean(filterClientId) || filterVatEnabled !== null;

    useEffect(() => {
        if (!show) return;
        if (!draftClientId) {
            setDraftClient(null);
            return;
        }
        if (draftClient?.ID === draftClientId) return;

        let cancelled = false;
        (async () => {
            const resClient = await apiClient.getClientById(draftClientId);
            if (cancelled) return;
            setDraftClient(resClient || null);
            if (!resClient) setDraftClientId(null);
        })();

        return () => {
            cancelled = true;
        };
    }, [show, draftClientId, draftClient?.ID]);

    const handleHide = () => setShow(false);

    const handleOpen = () => {
        setDraftClientId(filterClientId);
        setDraftVatEnabled(filterVatEnabled);
        setShow(true);
    };

    const handleChangeClient = (client: ClientType | null) => {
        setDraftClient(client);
        setDraftClientId(client?.ID ? Number(client.ID) : null);
    };

    const handleApply = () => {
        setFilterClientId(draftClientId);
        setFilterVatEnabled(draftVatEnabled);
        handleHide();
    };

    const handleReset = () => {
        setFilterClientId(null);
        setFilterVatEnabled(null);
        handleHide();
    };

    return (
        <>
            <EToolTip title={t('subjects.toolFilterClient')}>
                <span className="d-inline-block">
                    <Button
                        className="fs-14 px-2"
                        size="sm"
                        variant={filterActive ? 'danger' : 'primary'}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            handleOpen();
                        }}
                    >
                        <SolarIcon name="funnel" className="fs-14 p-0" />
                    </Button>
                </span>
            </EToolTip>

            <Offcanvas show={show} onHide={handleHide} placement="end">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('subjects.toolFilterClient')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-filter-form d-flex flex-column h-100">
                        <div className="off-filter-content px-3">
                            <div className="mb-3">
                                <ClientSelected
                                    title={t('subjects.filterClient')}
                                    className="mb-0"
                                    allowNew={false}
                                    clearable
                                    maxHeight={160}
                                    value={draftClient}
                                    onChange={handleChangeClient}
                                />
                            </div>

                            <div className="mb-3">
                                <div className="form-label text-muted mb-2">{t('subjects.filterVat')}</div>
                                {(
                                    [
                                        { value: null, label: t('subjects.filterVatAll') },
                                        { value: 1, label: t('subjects.filterVatYes') },
                                        { value: 0, label: t('subjects.filterVatNo') },
                                    ] as const
                                ).map((option) => (
                                    <label
                                        key={String(option.value)}
                                        className="d-flex align-items-center mb-1"
                                    >
                                        <input
                                            type="radio"
                                            className="form-check-input me-2"
                                            name="customers-filter-vat"
                                            checked={draftVatEnabled === option.value}
                                            onChange={() => setDraftVatEnabled(option.value)}
                                        />
                                        {option.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="p-3 offcanvas-footer off-filter-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" onClick={handleReset}>
                                    {t('common:buttons.reset')}
                                </Button>
                                <Button variant="info" className="min100px" onClick={handleApply}>
                                    {t('common:buttons.apply')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default CustomersFilter;
